import { createActions } from 'redux-actions';

import { ENDPOINTS_AGREEMENTS, ENDPOINTS_COMPETITIONS, ENDPOINTS_TEAMS } from '../config';
import { localeData } from '../locales/data';
import { defaultErrorMessage, globalFetch, isAction } from '../shared/helpers/fetch-helper';
import { logErrorService } from '../shared/services';

import { addFlashMessages } from './FlashMessagesActions';
import { invalidRequest } from './GenericActions';

export const {
  requestAgreement,
  responseAgreementFailure,
  responseAgreementSuccess,
  requestAgreementState,
  responseAgreementStateFailure,
  responseAgreementStateSuccess,
  setAgreementOpen,
  setAgreementClosed,
  resetAgreementsState,
} = createActions(
  {},
  'REQUEST_AGREEMENT',
  'RESPONSE_AGREEMENT_FAILURE',
  'RESPONSE_AGREEMENT_SUCCESS',
  'REQUEST_AGREEMENT_STATE',
  'RESPONSE_AGREEMENT_STATE_FAILURE',
  'RESPONSE_AGREEMENT_STATE_SUCCESS',
  'SET_AGREEMENT_OPEN',
  'SET_AGREEMENT_CLOSED',
  'RESET_AGREEMENTS_STATE'
);

const endpoints = {
  agreement: ENDPOINTS_AGREEMENTS.agreements,
  team: ENDPOINTS_TEAMS.teams,
  competition: ENDPOINTS_COMPETITIONS.competitions,
};

export const getAgreements =
  (id: string, endpoint: 'agreement' | 'team' | 'competition') =>
  async (dispatch: any, getState: () => any): Promise<object> => {
    if (!id || !endpoint || !endpoints[endpoint]) {
      return dispatch(responseAgreementFailure());
    }
    const state = getState();
    if (state.getIn(['agreements', 'agreements', id]) || state.getIn(['agreements', 'isFetching'])) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAgreement());

    return globalFetch(
      `${endpoints[endpoint]}/${id}/agreement`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseAgreementFailure())
    )
      .then((json: any) => {
        if (isAction(json, 'RESPONSE_AGREEMENT_FAILURE')) {
          return json;
        } else if (json.detail) {
          logErrorService(json.detail, json.detail);

          return dispatch(responseAgreementFailure());
        } else {
          return dispatch(responseAgreementSuccess(Object.assign({}, json, { customId: id })));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(responseAgreementFailure());
      });
  };

export const actionAgreementState = async (
  id: string,
  type: 'agreements' | 'teams' | 'competitions',
  agreementState: string,
  dispatch: any,
  getState: () => any
): Promise<object> => {
  const state = getState();
  if (state.getIn(['agreements', 'isFetching'])) {
    return new Promise((resolve: any) => resolve({ type: 'RESPONSE_AGREEMENT_STATE_FAILURE' }));
  }
  dispatch(requestAgreementState());

  const callbackStatus400 = (response: any) =>
    response.json().then((json: any) => {
      if (json && Array.isArray(json) && json.length !== 0) {
        const messages = json.map((message: string) => ({ msg: message, defaultMessage: localeData.en[message] }));
        dispatch(addFlashMessages(messages, { intl: false }));
      }

      return dispatch(responseAgreementStateFailure());
    });

  return globalFetch(
    `${ENDPOINTS_AGREEMENTS.accept}/${type}/${id}/${agreementState}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    },
    dispatch,
    () => dispatch(responseAgreementStateFailure()),
    {
      answerEmpty: true,
      callbackStatus400,
    }
  )
    .then(async (json: any): Promise<object> => {
      if (isAction(json, 'RESPONSE_AGREEMENT_STATE_FAILURE')) {
        return json;
      } else if (json && json.detail) {
        logErrorService(json.detail, json.detail);
        dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }], { intl: false }));

        return dispatch(responseAgreementStateFailure());
      } else {
        return dispatch(responseAgreementStateSuccess());
      }
    })
    .catch((err: any) => {
      logErrorService(err, err);

      return dispatch(responseAgreementStateFailure());
    });
};

export const agreementAccept =
  (id: string, type: 'agreements' | 'teams' | 'competitions') => async (dispatch: any, getState: () => any) => {
    return actionAgreementState(id, type, 'accept', dispatch, getState);
  };

export const agreementReject =
  (id: string, type: 'agreements' | 'teams' | 'competitions') => async (dispatch: any, getState: () => any) => {
    return actionAgreementState(id, type, 'reject', dispatch, getState);
  };

export const getAgreementById =
  (id: string) =>
  async (dispatch: any, getState: () => any): Promise<object> => {
    const state = getState();
    if (state.getIn(['agreements', 'agreements', id]) || state.getIn(['agreements', 'isFetching'])) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAgreement());

    return globalFetch(
      `${ENDPOINTS_AGREEMENTS.agreements}/${id}/agreement`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseAgreementFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_AGREEMENT_FAILURE')) {
          return json;
        } else if (json.detail) {
          logErrorService(json.detail, json.detail);

          return dispatch(responseAgreementFailure());
        } else {
          return dispatch(responseAgreementSuccess(json));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(responseAgreementFailure());
      });
  };
export const getCompetitionAgreementById =
  (id: string) =>
  async (dispatch: any, getState: () => any): Promise<object> => {
    const state = getState();

    if (state.getIn(['agreements', 'agreements', id]) || state.getIn(['agreements', 'isFetching'])) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAgreement());

    return globalFetch(
      `${ENDPOINTS_COMPETITIONS.competitions}/${id}/agreement`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(responseAgreementFailure())
    )
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_AGREEMENT_FAILURE')) {
          return json;
        } else if (json.detail) {
          logErrorService(json.detail, json.detail);

          return dispatch(responseAgreementFailure());
        }

        return dispatch(responseAgreementSuccess(json));
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(responseAgreementFailure());
      });
  };
